import React, { useEffect, useId, useMemo, useState } from 'react';
import {
    AlertTriangle,
    ArrowDown,
    ArrowUp,
    BookOpen,
    ChevronDown,
    ChevronRight,
    Eye,
    EyeOff,
    Loader2,
    Pencil,
    Plus,
    RotateCcw,
    Search,
    Trash2,
    X,
} from 'lucide-react';
import { KatalogPikto } from '../KatalogPikto';
import {
    OVERLAY_WARNGRENZE_BYTES,
    alsGesehenMarkieren,
    aufStandardZuruecksetzen,
    eigenenKnotenAnlegen,
    eigenenKnotenLoeschen,
    haeufigkeitSortierungSetzen,
    katalogEintragUebernehmen,
    knotenAendern,
    knotenAusblenden,
    overlayGroesseBytes,
    verschieben,
} from '../../../services/mangelKatalog/editorLogik';
import { eintragNachId } from '../../../services/mangelKatalog/auswahl';
import { gesamtkatalogJetzt, ladeGesamtkatalog, useGesamtkatalog } from '../../../services/mangelKatalog/laden';
import { PIKTO_SCHLUESSEL, piktoAufloesen } from '../../../services/mangelKatalog/piktoRegister';
import { KATALOG_VERSION, STANDARD_KATALOG } from '../../../services/mangelKatalog/standard';
import { useKatalogStand } from '../../../services/mangelKatalog/store';
import type { AufgeloesterKatalog, KatalogEintrag, KatalogOverlay, KnotenTyp } from '../../../services/mangelKatalog/typen';
import {
    FRIST_MAX,
    FRIST_MIN,
    KURZ_MAX_MANGEL,
    KURZ_MAX_ZEILE,
    MERKER_TEXT,
    NAME_MAX,
    editorKatalogBauen,
    eigenerKnotenAusFormular,
    eintragTrefferSuchen,
    englischerStandardVon,
    formularAus,
    formularPruefen,
    formularZuFelder,
    groesseText,
    istZuGross,
    kindTyp,
    leeresFormular,
    neuesOverlay,
    overlayZusammenfassung,
    rohKnoten,
    zeileBauen,
    type Formular,
    type Merker,
} from './editorAnsicht';

/**
 * Admin-Editor des Firmen-Mängelkatalogs („Standardmangel per Torte“,
 * Paket H; docs/mangelkatalog-plan.md, Entscheid „Pflege“).
 *
 * Baum Kategorie → Gruppe → Mangel über dem Standard samt Firmen-Overlay.
 * Ausgeblendete Knoten bleiben sichtbar (grau), damit sie sich wieder
 * einblenden lassen. Jede Aktion baut über editorLogik.ts ein NEUES Overlay
 * und gibt es an `onChange` — gespeichert wird beim Aufrufer (Autosave in
 * AdminGlobalSettings). Kein Browser-Dialog: Zurücksetzen und Löschen fragen
 * inline nach.
 */
export interface MangelKatalogEditorProps {
    /** Eigenes Overlay (profiles.settings.mangelKatalog, normalisiert) oder null = reiner Standard. */
    overlay: KatalogOverlay | null;
    /** Nach jeder Änderung das neue Overlay; null = „Alles auf Standard“ (Block entfernen). */
    onChange: (o: KatalogOverlay | null) => void;
    /** Eigene Profil-ID — Hinweis, wenn der Katalog eines anderen Admins wirkt. */
    eigeneProfilId?: string;
    /** Beim Öffnen aufgeklappte Knoten (sonst ist alles zu). */
    anfangsOffen?: readonly string[];
}

type Bearbeitung = { art: 'aendern'; id: string } | { art: 'anlegen'; typ: KnotenTyp; eltern: string | null };

type Rueckfrage = { id: string; aktion: 'zuruecksetzen' | 'loeschen' };

const MAX_TREFFER = 30;

// ─── Stil (warme Neutraltöne, ein Tiefenmittel je Element, Trefferflächen ≥ 44 px) ──

const FOKUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400';
const KNOPF_SYMBOL = `inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-stone-500 hover:bg-stone-100 hover:text-stone-900 disabled:pointer-events-none disabled:opacity-30 ${FOKUS}`;
const KNOPF_TEXT = `inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 text-sm font-semibold text-stone-700 hover:bg-stone-100 disabled:pointer-events-none disabled:opacity-40 ${FOKUS}`;
const KNOPF_HAUPT = `inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-stone-800 px-4 text-sm font-semibold text-white hover:bg-stone-700 ${FOKUS}`;
const KNOPF_FRAGE_GELB = `inline-flex min-h-11 items-center rounded-lg bg-amber-100 px-3 text-xs font-semibold text-amber-900 hover:bg-amber-200 ${FOKUS}`;
const KNOPF_FRAGE_ROT = `inline-flex min-h-11 items-center rounded-lg bg-red-100 px-3 text-xs font-semibold text-red-800 hover:bg-red-200 ${FOKUS}`;
const EINGABE = 'w-full min-h-11 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-400';
const BESCHRIFTUNG = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500';
const HINWEIS = 'mt-1 text-[11px] leading-snug text-stone-500';
/** Einrückung je Ebene (feste Klassen, damit Tailwind sie findet). */
const EINRUECKUNG = ['pl-1', 'pl-6', 'pl-11', 'pl-16'];
const einrueckung = (ebene: number): string => EINRUECKUNG[Math.min(ebene, EINRUECKUNG.length - 1)];

const MERKER_FARBE: Readonly<Record<Merker, string>> = {
    neu: 'bg-emerald-100 text-emerald-800',
    eigen: 'bg-sky-100 text-sky-800',
    geaendert: 'bg-amber-100 text-amber-900',
    ausgeblendet: 'bg-stone-200 text-stone-700',
};

const MerkerChip: React.FC<{ merker: Merker; text?: string; titel?: string }> = ({ merker, text, titel }) => (
    <span
        className={`shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-semibold leading-none ${MERKER_FARBE[merker]}`}
        title={titel}
        data-merker={merker}
    >
        {text ?? MERKER_TEXT[merker]}
    </span>
);

const Feld: React.FC<{ id: string; label: string; hinweis?: string; children: React.ReactNode }> = ({ id, label, hinweis, children }) => (
    <div>
        <label htmlFor={id} className={BESCHRIFTUNG}>
            {label}
        </label>
        {children}
        {hinweis && <p className={HINWEIS}>{hinweis}</p>}
    </div>
);

// ─── Piktogramm-Auswahl ─────────────────────────────────────────────────────

const PiktoAuswahl: React.FC<{
    wert: string;
    /** Piktogramm, das ohne eigene Wahl gilt. */
    erbPikto: string;
    /** Beschriftung der Wahl „kein eigenes“ („Standard“, „Erben“ …). */
    erbKurz: string;
    onWahl: (schluessel: string) => void;
}> = ({ wert, erbPikto, erbKurz, onWahl }) => {
    const [offen, setOffen] = useState(false);
    const titelId = useId();
    const wirksam = wert || erbPikto;
    const klasse = (aktiv: boolean) =>
        `flex h-11 w-11 items-center justify-center rounded-lg ${aktiv ? 'bg-stone-300' : 'hover:bg-stone-100'} ${FOKUS}`;
    return (
        <div>
            <span id={titelId} className={BESCHRIFTUNG}>
                Piktogramm
            </span>
            <div className="flex flex-wrap items-center gap-3">
                <KatalogPikto schluessel={wirksam} groesse={36} />
                <span className="min-w-0 flex-1 text-sm text-stone-700">
                    {piktoAufloesen(wirksam).titel}
                    {!wert && <span className="text-stone-500"> · {erbKurz}</span>}
                </span>
                <button type="button" className={KNOPF_TEXT} aria-expanded={offen} onClick={() => setOffen((o) => !o)}>
                    {offen ? 'Auswahl schließen' : 'Ändern'}
                </button>
            </div>
            {offen && (
                <div
                    role="group"
                    aria-labelledby={titelId}
                    className="mt-2 grid grid-cols-[repeat(auto-fill,minmax(2.75rem,1fr))] gap-1 rounded-lg border border-stone-200 bg-white p-2"
                >
                    <button type="button" className={klasse(!wert)} aria-pressed={!wert} title={erbKurz} onClick={() => onWahl('')}>
                        <span className="text-[11px] font-semibold leading-tight text-stone-700">{erbKurz}</span>
                    </button>
                    {PIKTO_SCHLUESSEL.map((s) => {
                        const titel = piktoAufloesen(s).titel;
                        return (
                            <button
                                type="button"
                                key={s}
                                className={klasse(wert === s)}
                                aria-pressed={wert === s}
                                aria-label={titel}
                                title={titel}
                                onClick={() => onWahl(s)}
                            >
                                <KatalogPikto schluessel={s} groesse={28} titel={titel} />
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── Formular (Bearbeiten und Anlegen) ──────────────────────────────────────

const DRINGLICHKEITEN: readonly (readonly [Formular['dringlichkeit'], string])[] = [
    ['', 'keine'],
    ['sofort', 'sofort'],
    ['frist', 'Frist'],
];

export interface KnotenFormularProps {
    typ: KnotenTyp;
    titel: string;
    start: Formular;
    /** Werte des Standards (nur Standardknoten). */
    vergleich: Formular | null;
    erbPikto: string;
    erbKurz: string;
    onSpeichern: (f: Formular) => void;
    onAbbrechen: () => void;
}

export const KnotenFormular: React.FC<KnotenFormularProps> = ({
    typ,
    titel,
    start,
    vergleich,
    erbPikto,
    erbKurz,
    onSpeichern,
    onAbbrechen,
}) => {
    const [f, setF] = useState<Formular>(start);
    const [fehler, setFehler] = useState<string | null>(null);
    const id = useId();
    const setze = <K extends keyof Formular>(feld: K, wert: Formular[K]) => {
        setF((prev) => ({ ...prev, [feld]: wert }));
        setFehler(null);
    };
    const speichern = (e: React.FormEvent) => {
        e.preventDefault();
        const problem = formularPruefen(f, typ);
        if (problem) {
            setFehler(problem);
            return;
        }
        onSpeichern(f);
    };
    const standardDringlichkeit = vergleich?.dringlichkeit
        ? vergleich.dringlichkeit === 'sofort'
            ? 'sofort'
            : `Frist ${vergleich.fristTage || '14'} Tage`
        : null;

    return (
        <form
            onSubmit={speichern}
            onKeyDown={(e) => {
                if (e.key === 'Escape') onAbbrechen();
            }}
            aria-label={titel}
            className="space-y-3 border-t border-stone-200 bg-stone-50 px-3 py-3"
        >
            <p className="text-sm font-semibold text-stone-800">{titel}</p>
            {typ === 'mangel' ? (
                <Feld id={`${id}-kurz`} label="Kurztext der Kachel" hinweis={`Höchstens ${KURZ_MAX_MANGEL} Zeichen.`}>
                    <input
                        id={`${id}-kurz`}
                        type="text"
                        className={EINGABE}
                        value={f.kurz}
                        placeholder={vergleich?.kurz}
                        onChange={(e) => setze('kurz', e.target.value)}
                    />
                </Feld>
            ) : (
                <Feld
                    id={`${id}-kurz`}
                    label="Kurztext im Tortenstück"
                    hinweis={`Höchstens zwei Zeilen à ${KURZ_MAX_ZEILE} Zeichen; Enter bricht um, ein Trennstrich am Zeilenende trennt das Wort.`}
                >
                    <textarea
                        id={`${id}-kurz`}
                        rows={2}
                        className={`${EINGABE} resize-none`}
                        value={f.kurz}
                        placeholder={vergleich?.kurz}
                        onChange={(e) => setze('kurz', e.target.value)}
                    />
                </Feld>
            )}
            {typ !== 'mangel' && (
                <Feld id={`${id}-name`} label="Name" hinweis={`Für Kopfzeile, Chip und Filter, höchstens ${NAME_MAX} Zeichen; leer = Kurztext.`}>
                    <input
                        id={`${id}-name`}
                        type="text"
                        className={EINGABE}
                        value={f.name}
                        placeholder={vergleich?.name}
                        onChange={(e) => setze('name', e.target.value)}
                    />
                </Feld>
            )}
            <PiktoAuswahl wert={f.pikto} erbPikto={erbPikto} erbKurz={erbKurz} onWahl={(s) => setze('pikto', s)} />
            {typ === 'mangel' && (
                <>
                    <Feld id={`${id}-titel`} label="Titel">
                        <input
                            id={`${id}-titel`}
                            type="text"
                            className={EINGABE}
                            value={f.titel}
                            placeholder={vergleich?.titel}
                            onChange={(e) => setze('titel', e.target.value)}
                        />
                    </Feld>
                    <Feld id={`${id}-text`} label="Mängeltext">
                        <textarea
                            id={`${id}-text`}
                            rows={4}
                            className={EINGABE}
                            value={f.text}
                            placeholder={vergleich?.text}
                            onChange={(e) => setze('text', e.target.value)}
                        />
                    </Feld>
                    <Feld
                        id={`${id}-vorschrift`}
                        label="Vorschrift (gekürzt, so steht sie im Bericht)"
                    >
                        <input
                            id={`${id}-vorschrift`}
                            type="text"
                            className={EINGABE}
                            value={f.vorschrift}
                            placeholder={vergleich?.vorschrift}
                            onChange={(e) => setze('vorschrift', e.target.value)}
                        />
                    </Feld>
                    <fieldset>
                        <legend className={BESCHRIFTUNG}>Dringlichkeit</legend>
                        <div className="flex flex-wrap items-center gap-2">
                            {DRINGLICHKEITEN.map(([wert, text]) => {
                                const aktiv = f.dringlichkeit === wert;
                                return (
                                    <button
                                        type="button"
                                        key={wert || 'keine'}
                                        aria-pressed={aktiv}
                                        onClick={() => {
                                            setze('dringlichkeit', wert);
                                            if (wert === 'frist' && !f.fristTage.trim()) setze('fristTage', '14');
                                        }}
                                        className={`min-h-11 rounded-lg border px-3 text-sm font-semibold ${FOKUS} ${
                                            aktiv
                                                ? 'border-stone-800 bg-stone-800 text-white'
                                                : 'border-stone-300 bg-white text-stone-700 hover:bg-stone-100'
                                        }`}
                                    >
                                        {wert === '' && standardDringlichkeit ? `Standard (${standardDringlichkeit})` : text}
                                    </button>
                                );
                            })}
                            {f.dringlichkeit === 'frist' && (
                                <label className="flex items-center gap-2 text-sm text-stone-700">
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        min={FRIST_MIN}
                                        max={FRIST_MAX}
                                        className={`${EINGABE} w-24`}
                                        value={f.fristTage}
                                        aria-label="Frist in Tagen"
                                        onChange={(e) => setze('fristTage', e.target.value)}
                                    />
                                    Tage
                                </label>
                            )}
                        </div>
                        <p className={HINWEIS}>Ohne Angabe bleibt die übliche Frist des Eintrags (+14 Tage).</p>
                    </fieldset>
                </>
            )}
            <fieldset className="space-y-3 rounded-lg border border-stone-200 bg-white p-3">
                <legend className={`${BESCHRIFTUNG} px-1`}>Englisch (optional)</legend>
                <p className={HINWEIS}>
                    Für Berichte in englischer Sprache. Leer = englische Standardfassung, bei eigenen Einträgen der deutsche Text.
                </p>
                {typ === 'mangel' ? (
                    <Feld id={`${id}-kurzEn`} label="Kurztext der Kachel (englisch)" hinweis={`Höchstens ${KURZ_MAX_MANGEL} Zeichen.`}>
                        <input
                            id={`${id}-kurzEn`}
                            type="text"
                            lang="en"
                            className={EINGABE}
                            value={f.kurzEn}
                            placeholder={vergleich?.kurzEn}
                            onChange={(e) => setze('kurzEn', e.target.value)}
                        />
                    </Feld>
                ) : (
                    <>
                        <Feld
                            id={`${id}-kurzEn`}
                            label="Kurztext im Tortenstück (englisch)"
                            hinweis={`Höchstens zwei Zeilen à ${KURZ_MAX_ZEILE} Zeichen.`}
                        >
                            <textarea
                                id={`${id}-kurzEn`}
                                rows={2}
                                lang="en"
                                className={`${EINGABE} resize-none`}
                                value={f.kurzEn}
                                placeholder={vergleich?.kurzEn}
                                onChange={(e) => setze('kurzEn', e.target.value)}
                            />
                        </Feld>
                        <Feld id={`${id}-nameEn`} label="Name (englisch)" hinweis={`Höchstens ${NAME_MAX} Zeichen; leer = Kurztext.`}>
                            <input
                                id={`${id}-nameEn`}
                                type="text"
                                lang="en"
                                className={EINGABE}
                                value={f.nameEn}
                                placeholder={vergleich?.nameEn}
                                onChange={(e) => setze('nameEn', e.target.value)}
                            />
                        </Feld>
                    </>
                )}
                {typ === 'mangel' && (
                    <>
                        <Feld id={`${id}-titelEn`} label="Titel (englisch)">
                            <input
                                id={`${id}-titelEn`}
                                type="text"
                                lang="en"
                                className={EINGABE}
                                value={f.titelEn}
                                placeholder={vergleich?.titelEn}
                                onChange={(e) => setze('titelEn', e.target.value)}
                            />
                        </Feld>
                        <Feld id={`${id}-textEn`} label="Mängeltext (englisch)" hinweis="Die Vorschrift wird automatisch in die englische Kurzform übertragen.">
                            <textarea
                                id={`${id}-textEn`}
                                rows={3}
                                lang="en"
                                className={EINGABE}
                                value={f.textEn}
                                placeholder={vergleich?.textEn}
                                onChange={(e) => setze('textEn', e.target.value)}
                            />
                        </Feld>
                    </>
                )}
            </fieldset>
            {vergleich && <p className={HINWEIS}>Unveränderte oder geleerte Felder behalten den Standard.</p>}
            {fehler && (
                <p role="alert" className="text-sm font-semibold text-red-700">
                    {fehler}
                </p>
            )}
            <div className="flex flex-wrap gap-2">
                <button type="submit" className={KNOPF_HAUPT}>
                    Speichern
                </button>
                <button type="button" className={KNOPF_TEXT} onClick={onAbbrechen}>
                    Abbrechen
                </button>
            </div>
        </form>
    );
};

// ─── Aus dem Gesamtkatalog übernehmen ───────────────────────────────────────

export const GesamtkatalogSuche: React.FC<{
    gruppeId: string;
    gruppeName: string;
    voll: AufgeloesterKatalog;
    onUebernehmen: (e: KatalogEintrag) => void;
    onSchliessen: () => void;
}> = ({ gruppeId, gruppeName, voll, onUebernehmen, onSchliessen }) => {
    const { quelle, laedt, fehler, nochmal } = useGesamtkatalog();
    // Englische Fassung vorladen: übernommene Mängel bekommen dann auch ihre englischen Texte (28.09.).
    useEffect(() => {
        void ladeGesamtkatalog('en');
    }, []);
    const [eingabe, setEingabe] = useState('');
    const id = useId();
    const treffer = useMemo(() => eintragTrefferSuchen(eingabe, quelle, voll, gruppeId, MAX_TREFFER), [eingabe, quelle, voll, gruppeId]);
    const titel = `Aus dem Gesamtkatalog in „${gruppeName}“ übernehmen`;
    return (
        <section aria-label={titel} className="space-y-3 border-t border-stone-200 bg-stone-50 px-3 py-3">
            <div className="flex items-center gap-2">
                <p className="min-w-0 flex-1 text-sm font-semibold text-stone-800">{titel}</p>
                <button type="button" className={KNOPF_SYMBOL} aria-label="Suche schließen" title="Suche schließen" onClick={onSchliessen}>
                    <X className="h-4 w-4" aria-hidden="true" />
                </button>
            </div>
            <div className="relative">
                <label htmlFor={id} className="sr-only">
                    Stichwort
                </label>
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" aria-hidden="true" />
                <input
                    id={id}
                    type="search"
                    className={`${EINGABE} pl-9`}
                    placeholder="Stichwort, z. B. Bordbrett oder Seitenschutz"
                    value={eingabe}
                    onChange={(e) => setEingabe(e.target.value)}
                />
            </div>
            {!quelle && (laedt || !fehler) && (
                <p className="flex items-center gap-2 text-xs text-stone-600">
                    <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden="true" />
                    Gesamtkatalog lädt …
                </p>
            )}
            {!quelle && fehler && !laedt && (
                <div className="flex flex-wrap items-center gap-2 text-sm text-amber-900">
                    <span>Der Gesamtkatalog ist nicht geladen (kein Netz?).</span>
                    <button type="button" className={KNOPF_TEXT} onClick={nochmal}>
                        Erneut versuchen
                    </button>
                </div>
            )}
            {quelle && !eingabe.trim() && <p className={HINWEIS}>Stichwort eingeben — alle Suchwörter müssen treffen, Umlaute sind egal.</p>}
            {quelle && eingabe.trim() !== '' && treffer.length === 0 && <p className="text-sm text-stone-600">Keine Treffer.</p>}
            {treffer.length > 0 && (
                <ul className="divide-y divide-stone-200 rounded-lg border border-stone-200 bg-white">
                    {treffer.map((t) => (
                        <li key={t.eintrag.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-stone-800">{t.eintrag.titel}</p>
                                {t.eintrag.text && <p className="line-clamp-2 text-xs text-stone-600">{t.eintrag.text}</p>}
                                {t.auchIn.length > 0 && <p className="text-[11px] text-stone-500">Auch in: {t.auchIn.join(' · ')}</p>}
                            </div>
                            {t.inGruppe ? (
                                <span className="text-[11px] font-semibold text-stone-500">schon in dieser Gruppe</span>
                            ) : (
                                <button type="button" className={KNOPF_TEXT} onClick={() => onUebernehmen(t.eintrag)}>
                                    <Plus className="h-4 w-4" aria-hidden="true" />
                                    Übernehmen
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            {treffer.length >= MAX_TREFFER && <p className={HINWEIS}>Die ersten {MAX_TREFFER} Treffer — bitte die Suche eingrenzen.</p>}
        </section>
    );
};

// ─── Editor ─────────────────────────────────────────────────────────────────

export const MangelKatalogEditor: React.FC<MangelKatalogEditorProps> = ({ overlay, onChange, eigeneProfilId, anfangsOffen }) => {
    const stand = useKatalogStand();
    // ~400 Knoten: zweimal auflösen (wirksam + ohne Ausblenden) nur bei neuem Overlay.
    const ek = useMemo(() => editorKatalogBauen(STANDARD_KATALOG, overlay), [overlay]);
    const [offen, setOffen] = useState<ReadonlySet<string>>(() => new Set(anfangsOffen ?? []));
    const [bearbeitung, setBearbeitung] = useState<Bearbeitung | null>(null);
    const [rueckfrage, setRueckfrage] = useState<Rueckfrage | null>(null);
    const [allesRueckfrage, setAllesRueckfrage] = useState(false);
    const [sucheFuer, setSucheFuer] = useState<string | null>(null);

    const groesse = overlay ? overlayGroesseBytes(overlay) : 0;
    const fremd = stand.overlayProfil && stand.overlayProfil.id !== eigeneProfilId ? stand.overlayProfil : null;

    /** Ausgangspunkt jeder Änderung; ein neues Overlay führt den heutigen Standard als gesehen. */
    const basis = (): KatalogOverlay => overlay ?? neuesOverlay(KATALOG_VERSION);

    const weitergeben = (neu: KatalogOverlay | null) => {
        setRueckfrage(null);
        setAllesRueckfrage(false);
        onChange(neu);
    };

    const haeufigkeitAn = overlay?.nachHaeufigkeit !== false;
    // „An“ ist der Standard — ohne eigenen Katalog nichts anlegen.
    const haeufigkeitUmschalten = (an: boolean) => {
        if (!overlay && an) return;
        weitergeben(haeufigkeitSortierungSetzen(basis(), an));
    };

    const aufklappen = (ids: string[]) =>
        setOffen((prev) => {
            const n = new Set(prev);
            for (const id of ids) n.add(id);
            return n;
        });

    const umschalten = (id: string) =>
        setOffen((prev) => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });

    const bearbeitungOeffnen = (b: Bearbeitung | null) => {
        setSucheFuer(null);
        setRueckfrage(null);
        setBearbeitung(b);
    };

    /** Werte eines Standardknotens ohne Overlay (null bei eigenen Knoten). */
    const vergleichFuer = (id: string): Formular | null => {
        const std = rohKnoten(STANDARD_KATALOG, null, id);
        return std ? formularAus(ek.standard.knoten.get(id), std, englischerStandardVon(STANDARD_KATALOG, id)) : null;
    };

    const elternPikto = (eltern: string | null): string => (eltern ? ek.voll.knoten.get(eltern)?.pikto : undefined) ?? 'allgemein';

    const aenderungSpeichern = (id: string, f: Formular) => {
        const k = ek.voll.knoten.get(id);
        if (!k) return;
        const vergleich = k.herkunft === 'standard' ? vergleichFuer(id) : null;
        weitergeben(knotenAendern(basis(), id, formularZuFelder(f, k.typ, vergleich), KATALOG_VERSION));
        setBearbeitung(null);
    };

    const neuSpeichern = (typ: KnotenTyp, eltern: string | null, f: Formular) => {
        const id = 'eigen_' + crypto.randomUUID();
        weitergeben(eigenenKnotenAnlegen(basis(), eigenerKnotenAusFormular(f, typ, eltern), id));
        aufklappen([...(eltern ? [eltern] : []), ...(typ === 'mangel' ? [] : [id])]);
        setBearbeitung(null);
    };

    // Immer MIT englischer Fassung (sonst bliebe der eigene Eintrag dauerhaft ohne
    // Englisch — Codex-Review 28.09.). Bewusst OHNE await: ist der englische
    // Gesamtkatalog noch nicht da (die Suche lädt ihn beim Öffnen vor), wird er
    // angestoßen und nichts angelegt; der nächste Klick übernimmt dann synchron
    // auf dem aktuellen Stand — kein veraltetes Overlay nach dem Warten, keine
    // Doppelanlage aus einem wartenden Klick.
    const [uebernahmeFehler, setUebernahmeFehler] = useState<string | null>(null);
    const eintragUebernehmen = (gruppeId: string, e: KatalogEintrag): void => {
        const englisch = gesamtkatalogJetzt('en');
        if (!englisch) {
            setUebernahmeFehler('Die englische Fassung lädt noch (bei fehlendem Netz später). Bitte gleich noch einmal übernehmen.');
            void ladeGesamtkatalog('en');
            return;
        }
        setUebernahmeFehler(null);
        weitergeben(katalogEintragUebernehmen(basis(), gruppeId, e, 'eigen_' + crypto.randomUUID(), eintragNachId(englisch, e.id)));
    };

    const formularRendern = (b: Bearbeitung): React.ReactNode => {
        if (b.art === 'aendern') {
            const k = ek.voll.knoten.get(b.id);
            if (!k) return null;
            const vergleich = k.herkunft === 'standard' ? vergleichFuer(b.id) : null;
            const standardPikto = k.herkunft === 'standard' ? rohKnoten(STANDARD_KATALOG, null, b.id)?.pikto : undefined;
            return (
                <KnotenFormular
                    key={`aendern:${b.id}`}
                    typ={k.typ}
                    titel={`„${zeileBauen(ek, b.id)?.beschriftung ?? k.name}“ bearbeiten`}
                    start={formularAus(
                        k,
                        rohKnoten(STANDARD_KATALOG, overlay, b.id),
                        k.herkunft === 'standard' ? englischerStandardVon(STANDARD_KATALOG, b.id) : {},
                    )}
                    vergleich={vergleich}
                    erbPikto={standardPikto || elternPikto(k.eltern)}
                    erbKurz={k.herkunft === 'standard' ? 'Standard' : k.typ === 'kategorie' ? 'Keins' : 'Erben'}
                    onSpeichern={(f) => aenderungSpeichern(b.id, f)}
                    onAbbrechen={() => setBearbeitung(null)}
                />
            );
        }
        const eltern = b.eltern ? ek.voll.knoten.get(b.eltern) : undefined;
        const was = b.typ === 'kategorie' ? 'Eigene Kategorie' : b.typ === 'gruppe' ? 'Eigene Gruppe' : 'Eigener Mangel';
        return (
            <KnotenFormular
                key={`anlegen:${b.typ}:${b.eltern ?? ''}`}
                typ={b.typ}
                titel={eltern ? `${was} in „${eltern.name}“` : was}
                start={leeresFormular()}
                vergleich={null}
                erbPikto={elternPikto(b.eltern)}
                erbKurz={b.typ === 'kategorie' ? 'Keins' : 'Erben'}
                onSpeichern={(f) => neuSpeichern(b.typ, b.eltern, f)}
                onAbbrechen={() => setBearbeitung(null)}
            />
        );
    };

    const knotenRendern = (id: string): React.ReactNode => {
        const z = zeileBauen(ek, id);
        if (!z) return null;
        const k = z.knoten;
        const aufklappbar = k.typ !== 'mangel';
        const istOffen = aufklappbar && offen.has(id);
        const grau = z.merker.includes('ausgeblendet');
        const kind = kindTyp(k.typ);
        const wirdBearbeitet = bearbeitung?.art === 'aendern' && bearbeitung.id === id;
        const frage = rueckfrage?.id === id ? rueckfrage.aktion : null;
        const name = z.beschriftung;
        return (
            <li key={id} data-knoten={id}>
                <div className={`flex flex-wrap items-center gap-x-1 py-0.5 pr-1 ${einrueckung(z.ebene)} ${grau ? 'bg-stone-50' : ''}`}>
                    <button
                        type="button"
                        className={`flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-lg px-1 text-left hover:bg-stone-100 ${FOKUS}`}
                        aria-expanded={aufklappbar ? istOffen : undefined}
                        title={aufklappbar ? (istOffen ? 'Zuklappen' : 'Aufklappen') : 'Bearbeiten'}
                        onClick={() => (aufklappbar ? umschalten(id) : bearbeitungOeffnen(wirdBearbeitet ? null : { art: 'aendern', id }))}
                    >
                        {aufklappbar ? (
                            istOffen ? (
                                <ChevronDown className="h-4 w-4 shrink-0 text-stone-400" aria-hidden="true" />
                            ) : (
                                <ChevronRight className="h-4 w-4 shrink-0 text-stone-400" aria-hidden="true" />
                            )
                        ) : (
                            <span className="w-4 shrink-0" aria-hidden="true" />
                        )}
                        <KatalogPikto schluessel={k.pikto} groesse={k.typ === 'kategorie' ? 28 : 24} className={grau ? 'opacity-40' : undefined} />
                        <span className={`min-w-0 truncate text-sm font-semibold ${grau ? 'text-stone-400' : 'text-stone-800'}`}>{name}</span>
                        {aufklappbar && <span className="shrink-0 text-[11px] font-semibold text-stone-400">{z.kinder.length}</span>}
                        {z.merker.map((m) => (
                            <MerkerChip
                                key={m}
                                merker={m}
                                titel={m === 'ausgeblendet' && z.geerbtAusgeblendet ? 'Über einen Oberpunkt ausgeblendet' : undefined}
                            />
                        ))}
                        {z.neuDarunter > 0 && <MerkerChip merker="neu" text={`${z.neuDarunter} neu`} titel="Neue Standardeinträge darunter" />}
                    </button>
                    <div className="ml-auto flex flex-wrap items-center justify-end">
                        <button
                            type="button"
                            className={KNOPF_SYMBOL}
                            disabled={!z.kannHoch}
                            aria-label={`„${name}“ nach oben`}
                            title="Nach oben"
                            onClick={() => weitergeben(verschieben(basis(), ek.voll, id, -1))}
                        >
                            <ArrowUp className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            className={KNOPF_SYMBOL}
                            disabled={!z.kannRunter}
                            aria-label={`„${name}“ nach unten`}
                            title="Nach unten"
                            onClick={() => weitergeben(verschieben(basis(), ek.voll, id, 1))}
                        >
                            <ArrowDown className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            className={KNOPF_SYMBOL}
                            aria-pressed={wirdBearbeitet}
                            aria-label={`„${name}“ bearbeiten`}
                            title="Bearbeiten"
                            onClick={() => bearbeitungOeffnen(wirdBearbeitet ? null : { art: 'aendern', id })}
                        >
                            <Pencil className="h-4 w-4" aria-hidden="true" />
                        </button>
                        {z.selbstAusgeblendet ? (
                            <button
                                type="button"
                                className={`inline-flex min-h-11 items-center gap-1 rounded-lg px-2 text-xs font-semibold text-stone-700 hover:bg-stone-100 ${FOKUS}`}
                                aria-label={`„${name}“ einblenden`}
                                onClick={() => weitergeben(knotenAusblenden(basis(), id, false))}
                            >
                                <Eye className="h-4 w-4" aria-hidden="true" />
                                Einblenden
                            </button>
                        ) : (
                            <button
                                type="button"
                                className={KNOPF_SYMBOL}
                                aria-label={`„${name}“ ausblenden`}
                                title="Ausblenden"
                                onClick={() => weitergeben(knotenAusblenden(basis(), id, true))}
                            >
                                <EyeOff className="h-4 w-4" aria-hidden="true" />
                            </button>
                        )}
                        {k.herkunft === 'standard' &&
                            k.geaendert &&
                            (frage === 'zuruecksetzen' ? (
                                <>
                                    <button
                                        type="button"
                                        className={KNOPF_FRAGE_GELB}
                                        onClick={() => weitergeben(aufStandardZuruecksetzen(basis(), id))}
                                    >
                                        Wirklich zurücksetzen?
                                    </button>
                                    <button type="button" className={KNOPF_SYMBOL} aria-label="Nicht zurücksetzen" title="Abbrechen" onClick={() => setRueckfrage(null)}>
                                        <X className="h-4 w-4" aria-hidden="true" />
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    className={KNOPF_SYMBOL}
                                    aria-label={`„${name}“ auf Standard zurücksetzen`}
                                    title="Auf Standard zurücksetzen"
                                    onClick={() => setRueckfrage({ id, aktion: 'zuruecksetzen' })}
                                >
                                    <RotateCcw className="h-4 w-4" aria-hidden="true" />
                                </button>
                            ))}
                        {k.herkunft === 'eigen' &&
                            (frage === 'loeschen' ? (
                                <>
                                    <button
                                        type="button"
                                        className={KNOPF_FRAGE_ROT}
                                        onClick={() => {
                                            if (bearbeitung?.art === 'aendern' && bearbeitung.id === id) setBearbeitung(null);
                                            weitergeben(eigenenKnotenLoeschen(basis(), id));
                                        }}
                                    >
                                        {z.kinder.length > 0 ? `Wirklich löschen (mit ${z.kinder.length} darunter)?` : 'Wirklich löschen?'}
                                    </button>
                                    <button type="button" className={KNOPF_SYMBOL} aria-label="Nicht löschen" title="Abbrechen" onClick={() => setRueckfrage(null)}>
                                        <X className="h-4 w-4" aria-hidden="true" />
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    className={KNOPF_SYMBOL}
                                    aria-label={`„${name}“ löschen`}
                                    title="Eigenen Eintrag löschen"
                                    onClick={() => setRueckfrage({ id, aktion: 'loeschen' })}
                                >
                                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                                </button>
                            ))}
                    </div>
                </div>
                {wirdBearbeitet && bearbeitung && formularRendern(bearbeitung)}
                {istOffen && (
                    <>
                        {z.kinder.length > 0 ? (
                            <ul className="divide-y divide-stone-100 border-t border-stone-100">{z.kinder.map(knotenRendern)}</ul>
                        ) : (
                            <p className={`border-t border-stone-100 py-2 text-xs text-stone-500 ${einrueckung(z.ebene + 1)}`}>Noch keine Einträge.</p>
                        )}
                        {kind && (
                            <div className={`flex flex-wrap gap-2 border-t border-stone-100 py-2 pr-2 ${einrueckung(z.ebene + 1)}`}>
                                <button
                                    type="button"
                                    className={KNOPF_TEXT}
                                    onClick={() => bearbeitungOeffnen({ art: 'anlegen', typ: kind, eltern: id })}
                                >
                                    <Plus className="h-4 w-4" aria-hidden="true" />
                                    {kind === 'gruppe' ? 'Eigene Gruppe' : 'Eigener Mangel'}
                                </button>
                                {k.typ === 'gruppe' && (
                                    <button
                                        type="button"
                                        className={KNOPF_TEXT}
                                        aria-expanded={sucheFuer === id}
                                        onClick={() => {
                                            setBearbeitung(null);
                                            setSucheFuer(sucheFuer === id ? null : id);
                                        }}
                                    >
                                        <BookOpen className="h-4 w-4" aria-hidden="true" />
                                        Aus Gesamtkatalog übernehmen
                                    </button>
                                )}
                            </div>
                        )}
                        {bearbeitung?.art === 'anlegen' && bearbeitung.eltern === id && formularRendern(bearbeitung)}
                        {sucheFuer === id && (
                            <GesamtkatalogSuche
                                gruppeId={id}
                                gruppeName={k.name}
                                voll={ek.voll}
                                onUebernehmen={(e) => eintragUebernehmen(id, e)}
                                onSchliessen={() => {
                                    setSucheFuer(null);
                                    setUebernahmeFehler(null);
                                }}
                            />
                        )}
                        {sucheFuer === id && uebernahmeFehler && (
                            <p role="alert" className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] font-semibold text-amber-900">
                                {uebernahmeFehler}
                            </p>
                        )}
                    </>
                )}
            </li>
        );
    };

    const wurzel = ek.voll.kinder.get('') ?? [];
    const kategorieAnlegen = bearbeitung?.art === 'anlegen' && bearbeitung.typ === 'kategorie' ? bearbeitung : null;

    return (
        <div className="mt-4 space-y-4 text-stone-800">
            <div className="space-y-3">
                {fremd && (
                    <div role="status" className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                        <div className="min-w-0 space-y-2">
                            <p className="font-semibold">Wirksam ist der Katalog eines anderen Admins{fremd.name ? ` (${fremd.name})` : ''}.</p>
                            <p className="text-xs">
                                Hier wird der eigene Katalog dieses Profils gepflegt. Mitarbeiter ohne eigenen Katalog bekommen den des
                                ranghöchsten Admins (Super-Admin vor Admin) — eine Änderung hier kann den wirksamen Katalog also ablösen.
                            </p>
                            {!overlay && stand.overlay && (
                                <button type="button" className={KNOPF_TEXT} onClick={() => weitergeben(stand.overlay)}>
                                    Wirksamen Katalog als Ausgangspunkt übernehmen
                                </button>
                            )}
                        </div>
                    </div>
                )}
                <p className="text-xs leading-relaxed text-stone-600">
                    Ausblenden, geänderte Texte, eigene Einträge und die Reihenfolge wirken firmenweit in der Mängelerfassung (Torte,
                    Kacheln, Suche). Neue Standardeinträge kommen mit Updates automatisch dazu. Rangregel: Wer einen eigenen Katalog hat,
                    sieht seinen; alle anderen den des ranghöchsten Admins (Super-Admin vor Admin).
                </p>
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-stone-200 bg-white p-3">
                    <input
                        type="checkbox"
                        className="mt-0.5 h-5 w-5 shrink-0 accent-stone-800"
                        checked={haeufigkeitAn}
                        onChange={(e) => haeufigkeitUmschalten(e.target.checked)}
                    />
                    <span className="min-w-0">
                        <span className="block text-sm font-semibold text-stone-800">Häufig gewählte Mängel automatisch nach vorn</span>
                        <span className={`block ${HINWEIS}`}>
                            Was die Firma oft wählt, rückt in Torte und Kacheln auf die ersten Plätze (gezählt über die Mängel der
                            letzten zwei Jahre, jüngere zählen mehr; einmal je Anmeldung aktualisiert). Die Reihenfolge hier gilt für
                            selten Gewähltes und bei gleicher Häufigkeit.
                        </span>
                    </span>
                </label>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="mr-auto text-xs font-semibold text-stone-600">
                        {overlayZusammenfassung(overlay)}
                        {overlay ? ` · ${groesseText(groesse)}` : ''}
                    </span>
                    <button
                        type="button"
                        className={KNOPF_TEXT}
                        disabled={ek.neuAnzahl === 0}
                        title={ek.neuAnzahl === 0 ? 'Keine neuen Standardeinträge' : undefined}
                        onClick={() => weitergeben(alsGesehenMarkieren(basis(), KATALOG_VERSION))}
                    >
                        Neue Standardeinträge als gesehen markieren{ek.neuAnzahl > 0 ? ` (${ek.neuAnzahl})` : ''}
                    </button>
                    {overlay &&
                        (allesRueckfrage ? (
                            <>
                                <button type="button" className={KNOPF_FRAGE_ROT} onClick={() => weitergeben(null)}>
                                    Wirklich alles auf Standard?
                                </button>
                                <button type="button" className={KNOPF_SYMBOL} aria-label="Nicht zurücksetzen" title="Abbrechen" onClick={() => setAllesRueckfrage(false)}>
                                    <X className="h-4 w-4" aria-hidden="true" />
                                </button>
                            </>
                        ) : (
                            <button type="button" className={KNOPF_TEXT} onClick={() => setAllesRueckfrage(true)}>
                                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                                Alles auf Standard
                            </button>
                        ))}
                </div>
                {istZuGross(groesse) && (
                    <div role="status" className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                        <p>
                            Der Katalog ist {groesseText(groesse)} groß (Richtwert {groesseText(OVERLAY_WARNGRENZE_BYTES)}). Er wird bei
                            jeder Anmeldung geladen — lange Texte kürzen oder eigene Einträge zusammenfassen.
                        </p>
                    </div>
                )}
            </div>

            <ul aria-label="Mängelkatalog" className="divide-y divide-stone-100 overflow-hidden rounded-xl border border-stone-200 bg-white">
                {wurzel.map(knotenRendern)}
            </ul>

            <div className="space-y-2">
                <button type="button" className={KNOPF_TEXT} onClick={() => bearbeitungOeffnen({ art: 'anlegen', typ: 'kategorie', eltern: null })}>
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Eigene Kategorie
                </button>
                {kategorieAnlegen && <div className="overflow-hidden rounded-xl border border-stone-200">{formularRendern(kategorieAnlegen)}</div>}
            </div>
        </div>
    );
};
